import React from 'react';
import { Outlet } from 'react-router-dom';

import avatar from 'assets/images/svg/avatar.svg';
import {
  ArrowIcon,
  CopyToClipboard,
  CustomLink,
  StatusBadge,
  StatusBadgeTone,
} from 'components/common';
import { useProfileData, useUserCode } from 'hooks';

import { Container, Section } from '../../../../../components';
import PrimaryButton from '../../../../../components/common/buttons/decorators/PrimaryButton';
import { PrimaryButtonColor } from '../../../../../components/common/buttons/decorators/PrimaryButton/PrimaryButton';
import Username from '../../../../../components/user/Username';
import hidePassportData from '../../../../../formatters/hidePassportData';
import hidePhone from '../../../../../formatters/hidePhone';
import { cn, RouteDictionary } from '../../../../../utils';
import styles from './Profile.module.scss';

const Profile = () => {
  const profileData = useProfileData();
  const userCode = useUserCode();

  const userEmail = (): string => {
    if (profileData.email === null) {
      return 'Не указан';
    }
    return profileData.email.email;
  };

  const identificationStatus = (): { label: string; tone: StatusBadgeTone } => {
    if (profileData.lenderVerified) {
      return { label: 'Идентификация пройдена', tone: 'green' };
    }
    if (profileData.hasIdentificationDraft) {
      return { label: 'На проверке', tone: 'yellow' };
    }
    return { label: 'Не пройдена', tone: 'muted' };
  };

  const needIdentification = !profileData.lenderVerified && !profileData.hasIdentificationDraft;
  const status = identificationStatus();

  return (
    <Container>
      <Section>
        <div className={styles.profile}>
          <nav className={styles.breadcrumbs}>
            <CustomLink to={RouteDictionary.MY_MONEY} className={styles.breadcrumbLink}>
              Главная
            </CustomLink>
            <span className={styles.breadcrumbSeparator}>/</span>
            <span className={styles.breadcrumbCurrent}>Профиль</span>
          </nav>

          <section className={cn(styles.card, styles.hero)}>
            <div className={styles.identity}>
              <div className={styles.avatar}>
                {profileData.gender !== null && <img src={avatar} alt="аватар" />}
              </div>
              <div>
                <h1 className={styles.name}>
                  <Username />
                </h1>
                <div className={styles.userId}>
                  <span>ID {userCode}</span>
                  {profileData.agentCode !== null && <CopyToClipboard textToCopy={userCode} />}
                </div>
              </div>
            </div>
            <div className={styles.actions}>
              <PrimaryButton large color={PrimaryButtonColor.GREEN}>
                <CustomLink to={RouteDictionary.PROFILE_CONTROL}>Управление профилем</CustomLink>
              </PrimaryButton>
              <CustomLink to={RouteDictionary.LOGOUT} className={styles.logout}>
                Выйти
                <ArrowIcon direction="right" color="currentColor" size={20} title="Выйти" />
              </CustomLink>
            </div>
          </section>

          <div className={styles.grid}>
            <section className={styles.card}>
              <div className={styles.cardHead}>
                <h2 className={styles.cardTitle}>Контактные данные</h2>
              </div>
              <div className={styles.rows}>
                <div className={styles.row}>
                  <span className={styles.rowLabel}>E-mail</span>
                  <span className={styles.rowValue}>{userEmail()}</span>
                </div>
                <div className={styles.row}>
                  <span className={styles.rowLabel}>Телефон</span>
                  <span className={styles.rowValue}>{hidePhone(profileData.phone)}</span>
                </div>
              </div>
            </section>

            <section className={styles.card}>
              <div className={styles.cardHead}>
                <h2 className={styles.cardTitle}>Идентификация</h2>
                <StatusBadge tone={status.tone}>{status.label}</StatusBadge>
              </div>
              <div className={styles.rows}>
                <div className={styles.row}>
                  <span className={styles.rowLabel}>Документ (Паспорт РФ)</span>
                  <span className={styles.rowValue}>
                    {profileData.lenderVerified
                      ? hidePassportData(profileData.personalData!.passport)
                      : 'Не указан'}
                  </span>
                </div>
              </div>
              {profileData.hasIdentificationDraft && (
                <div className={styles.notice}>
                  Ваши данные находятся на проверке у администратора. Обычно это занимает до 1-2
                  рабочих дней
                </div>
              )}
              {needIdentification && (
                <div className={styles.cardAction}>
                  <PrimaryButton expanded large color={PrimaryButtonColor.GREEN}>
                    <CustomLink to={RouteDictionary.IDENTIFICATION}>
                      Пройти идентификацию
                    </CustomLink>
                  </PrimaryButton>
                </div>
              )}
            </section>
          </div>

          <Outlet />
        </div>
      </Section>
    </Container>
  );
};

export default Profile;
